import { Add } from '@mui/icons-material';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import React, { useContext, useState } from 'react';
import { DisableContext } from '../../contexts/DisableContextProvider';
import { useCreateAttachments, useDestroyAttachments, useGetAttachmentsByOwner } from '../../services/react-query/attachments';
import { AttachmentOwnerType } from '../../types/Attachment';
import { UserSettingsCollapseKeys } from '../../types/User';
import { Loading } from '../../uikit';
import ConfirmationModal from '../ConfirmationModal';
import AttachmentTile from './AttachmentTile';
import UploadModal from './UploadModal';
import ResponsiveButton from '../../uikit/buttons/ResponsiveButton';
import AccordionSection from '../AccordionSection';

interface IAttachmentSectionProps {
  collapseId: UserSettingsCollapseKeys;
  ownerType: AttachmentOwnerType;
  ownerUuid: string;
}

export default function AttachmentsSection({ collapseId, ownerType, ownerUuid }: IAttachmentSectionProps) {
  const [uploadOpen, setUploadOpen] = useState<boolean>(false);
  const [keyToDestroy, setKeyToDestroy] = useState<string | null>(null);
  const { data: attachmentData } = useGetAttachmentsByOwner({ type: ownerType, uuid: ownerUuid });
  const { disabled } = useContext(DisableContext);
  const { mutate: uploadFiles } = useCreateAttachments({
    type: ownerType,
    uuid: ownerUuid,
    onSuccessCallback: () => setUploadOpen(false),
  });
  const { mutate: destroyFiles } = useDestroyAttachments({
    type: ownerType,
    uuid: ownerUuid,
    onSuccessCallback: () => setKeyToDestroy(null),
  });

  if (!ownerUuid || ownerUuid === 'new') return null;
  if (!attachmentData) return <Loading />;

  const actionButtons = (
    <ResponsiveButton disabled={disabled} startIcon={<Add />} variant="outlined" onClick={() => setUploadOpen(true)}>
      Add
    </ResponsiveButton>
  );

  return (
    <Box sx={{ marginBottom: { xs: 2, lg: 0 } }}>
      <AccordionSection title="Attachments" headerElements={actionButtons} collapseId={collapseId} hasData={attachmentData.length > 0}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>FILE NAME</TableCell>
                <TableCell>ATTACHED BY</TableCell>
                <TableCell>DATE ADDED</TableCell>
                <TableCell>SIZE</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attachmentData.map((attachment) => (
                <AttachmentTile key={attachment.key} attachment={attachment} onDelete={setKeyToDestroy} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <UploadModal isOpen={uploadOpen} onCancel={() => setUploadOpen(false)} onUpload={uploadFiles} />
        <ConfirmationModal
          text="Delete this file?"
          isOpen={keyToDestroy !== null}
          onConfirm={() => keyToDestroy && destroyFiles([keyToDestroy])}
          onCancel={() => setKeyToDestroy(null)}
        />
      </AccordionSection>
    </Box>
  );
}
